import React from "react";
import { CheckIcon, Filter, FileCheck, FileX, FileQuestion } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface DocumentStatusFilterProps {
  value: 'active' | 'expired' | 'missing' | undefined;
  onChange: (status: 'active' | 'expired' | 'missing' | undefined) => void;
}

export function DocumentStatusFilter({ value, onChange }: DocumentStatusFilterProps) {
  const statusOptions = [
    { value: 'active' as const, label: 'Active', icon: FileCheck, color: 'bg-green-100 text-green-800' },
    { value: 'expired' as const, label: 'Expired', icon: FileX, color: 'text-red-600 border-red-200' },
    { value: 'missing' as const, label: 'Missing', icon: FileQuestion, color: 'text-orange-600 border-orange-200' }
  ];

  const selectedStatus = value 
    ? statusOptions.find(status => status.value === value) 
    : undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span>Status</span>
          {value && (
            <Badge 
              variant={value === 'active' ? 'default' : 'outline'} 
              className={`ml-2 ${selectedStatus?.color}`}
            >
              {selectedStatus?.label || "Selected"}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Filter by document status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={!value}
          onClick={() => onChange(undefined)}
        >
          All Statuses
        </DropdownMenuCheckboxItem>
        {statusOptions.map((status) => {
          const IconComponent = status.icon;
          return (
            <DropdownMenuCheckboxItem 
              key={status.value}
              checked={value === status.value}
              onClick={() => onChange(status.value)}
              className="flex items-center"
            >
              <IconComponent className="mr-2 h-4 w-4" />
              {status.label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 